import {
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
  Spinner,
  useBreakpointValue,
  useToast,
  Icon,
  Button,
  Divider,
  IconButton,
  useClipboard,
  Box,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback } from "react";
import actions from "../../actions";
import {
  KYDOrganizationSettings,
  KYDOrganizationFee,
} from "@common/interfaces/KYDOrganization";
import { useDialog } from "../../Common/Dialog";
import { FaCheck, FaWindowClose } from "react-icons/fa";
import { showSuccessToast } from "../../utils";
import { FiCopy, FiInfo } from "react-icons/fi";
import MetaAdsSetting from "../MetaAdsSetting";
import { useOrganizationSettings } from "../hooks/useOrganizationSettings";
import { useFacebookMetaAdsConnect } from "../hooks/useFacebookMetaAdsConnect";
import { useStripe as useStripeHook } from "../hooks/useStripe";
import SettingToggle from "./SettingToggle";
import RebateForm from "./RebateForm";
import {
  calculateFeesFromStructure,
  calculatePreviewData as calculatePreviewDataFromAPI,
} from "../utils/feeCalculations";

const Settings = () => {
  const { organization, loading, updateSettings, updateRebate } =
    useOrganizationSettings();
  const { onCopy, hasCopied } = useClipboard(organization?.id);

  const [localLoading, setLocalLoading] = useState<{
    stripe?: boolean;
    meta?: boolean;
    screenshots?: boolean;
    reminder24h?: boolean;
    reminder1h?: boolean;
    rebate?: boolean;
  }>({});

  const getRebateFromFees = (fees?: KYDOrganizationFee[]) => {
    const rebateFee = fees?.find((fee) => fee.type === "rebate");
    if (!rebateFee) return null;

    // Return null if breaks array is empty or amount is 0
    const firstBreakAmount = rebateFee.breaks?.[0]?.amount;
    if (!firstBreakAmount || firstBreakAmount === 0) return null;

    return {
      mode: rebateFee.mode,
      // Rebates are stored in bips (basis points) for percentage mode
      // e.g., 500 bips = 5%
      amount: firstBreakAmount,
    };
  };

  const initialRebate = getRebateFromFees(organization?.fees);
  const [rebateConfig, setRebateConfig] = useState<{
    enabled: boolean;
    amount: string;
  }>({
    enabled: !!initialRebate,
    // Convert from bips to percentage display value (divide by 100)
    amount: initialRebate ? (initialRebate.amount / 100).toFixed(2) : "",
  });
  const { stripe: stripeObject } = useStripeHook({ organization });
  const rebateUiEnabled = !!organization?.settings?.rebates_enabled;
  const toast = useToast();
  const dialog = useDialog();

  const calculateFeesForForm = async (faceValue: number): Promise<number> => {
    try {
      const { total_fees } = await actions.ADMIN.calculateFinalTicketPrice(
        faceValue
      );
      return total_fees.getAmount() / 100;
    } catch (error) {
      console.error("Error calculating fees:", error);
      // Fallback to client-side calculation
      const fees = organization?.fees || [];
      return calculateFeesFromStructure(faceValue, fees);
    }
  };

  const updateLoading = useCallback((key: string, value: boolean) => {
    setLocalLoading((l: any) => {
      return { ...l, [key]: value };
    });
  }, []);

  const onLinkStripeAccount = useCallback(() => {
    window.location.href = `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${organization.stripe_oauth_client_id}&scope=read_write`;
  }, [organization, toast, dialog, updateSettings, updateLoading]);

  const onStripeRedirect = useCallback(
    async (code: string) => {
      updateLoading("stripe", true);
      try {
        await actions.ADMIN.updateStripeConnection({ code });
        // Refetch settings to get updated organization data
        await updateSettings({});
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("stripe", false);
    },
    [updateSettings]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code && organization && !organization.stripe_connect_id) {
      onStripeRedirect(code);
    }
  }, [organization]);

  useEffect(() => {
    const rebate = getRebateFromFees(organization?.fees);
    if (rebate) {
      setRebateConfig({
        enabled: true,
        amount: (rebate.amount / 100).toFixed(2),
      });
    } else {
      setRebateConfig({
        enabled: false,
        amount: "",
      });
    }
  }, [organization?.fees]);

  const handleRebateRemove = async () => {
    updateLoading("rebate", true);
    try {
      const success = await updateRebate(null);
      if (success) {
        toast({
          title: "Rebate removed",
          description: "Rebate has been removed from your organization",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      updateLoading("rebate", false);
    }
  };

  // Calculate preview data for rebate display
  const calculatePreviewData = useCallback(
    async (rebate: { amount: number }) => {
      return calculatePreviewDataFromAPI(
        rebate,
        actions.ADMIN.calculateFinalTicketPrice,
        undefined, // no event_id for organization-level
        calculateFeesForForm // fallback function
      );
    },
    [calculateFeesForForm]
  );

  // Format rebate display
  const formatRebateDisplay = (rebate: { amount: number }): string => {
    // Rebate amount is in bips, convert to percentage
    const percentage = rebate.amount / 100;
    return `${percentage.toFixed(2)}%`;
  };

  const onUpdateSettings = useCallback(
    async (settings: Partial<KYDOrganizationSettings>, message?: string) => {
      const success = await updateSettings(settings);
      if (success) {
        toast({
          title: "Settings Updated",
          description: message || "Your settings have been updated",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [updateSettings, toast]
  );

  const { triggerFacebookLogin } = useFacebookMetaAdsConnect({
    organization,
    toast,
    dialog,
    onAccessGranted: async () => {
      await updateSettings({});
    },
    onLoadingChange: (v) => updateLoading("meta", v),
  });

  const removeOrganizationAccess = useCallback(() => {
    updateLoading("meta", true);
    actions.ADMIN.removeOrganizationAccess().then(() => {
      toast({
        status: "success",
        title: "Access removed",
        description:
          "KYD access to Meta ads has been removed. You can no longer manage campaigns via KYD dashboard",
      });
      updateSettings({}).then(() => {
        updateLoading("meta", false);
      });
    });
  }, [updateSettings]);

  const isScreenshotProtectionEnabled =
    organization?.settings?.screenshot_disabled;

  return (
    <VStack w="100%">
      <Stack
        w="100%"
        maxW={"500px"}
        spacing={{ base: "8", lg: "6" }}
        py={[2, 6]}
      >
        <Stack
          spacing="4"
          direction={{ base: "column", lg: "row" }}
          justify="space-between"
          align={{ base: "start", lg: "center" }}
        >
          <Stack spacing="1">
            <Heading
              size={useBreakpointValue({ base: "xs", lg: "sm" })}
              fontWeight="medium"
            >
              <HStack>
                <Text fontSize={"lg"} fontWeight="bold">
                  Settings
                </Text>
              </HStack>
            </Heading>
          </Stack>
        </Stack>

        {loading.organization && !organization && (
          <VStack>
            <Spinner />
            <Text>Loading settings...</Text>
          </VStack>
        )}

        {organization && (
          <Stack spacing={{ base: "2", lg: "4" }}>
            <SettingToggle
              title="Screenshot Protection"
              status={
                isScreenshotProtectionEnabled ? (
                  <>
                    Attendees{" "}
                    <Text as="span" fontWeight="bold">
                      cannot
                    </Text>{" "}
                    check in with screenshots
                  </>
                ) : (
                  <>
                    Attendees{" "}
                    <Text as="span" fontWeight="bold">
                      can
                    </Text>{" "}
                    check in with screenshots
                  </>
                )
              }
              description="Enabling screenshot protection prevents attendees from checking in using screenshots of QR codes, improving security. May slow down the door. Applies to all events in your organization."
              isChecked={isScreenshotProtectionEnabled}
              isDisabled={localLoading.screenshots}
              isLoading={localLoading.screenshots}
              onChange={(checked) => {
                updateLoading("screenshots", true);
                onUpdateSettings(
                  {
                    screenshot_disabled: checked,
                  } as Partial<KYDOrganizationSettings>,
                  checked
                    ? "Screenshot protection enabled"
                    : "Screenshot protection disabled"
                ).finally(() => {
                  updateLoading("screenshots", false);
                });
              }}
              colorScheme="green"
              statusColor={
                isScreenshotProtectionEnabled ? "red.400" : "gray.500"
              }
            />
            <SettingToggle
              title="24 Hour Reminder Texts"
              status={
                !organization.settings.supress_first_reminder_sms
                  ? "Enabled"
                  : "Disabled"
              }
              isChecked={!organization.settings.supress_first_reminder_sms}
              isDisabled={localLoading.reminder24h}
              isLoading={localLoading.reminder24h}
              onChange={(checked) => {
                updateLoading("reminder24h", true);
                onUpdateSettings(
                  {
                    supress_first_reminder_sms: !checked,
                    supress_second_reminder_sms:
                      !!organization.settings.supress_second_reminder_sms,
                  },
                  checked
                    ? "24 hour reminder texts enabled"
                    : "24 hour reminder texts disabled"
                ).finally(() => {
                  updateLoading("reminder24h", false);
                });
              }}
              colorScheme="green"
            />
            <SettingToggle
              title="1 Hour Reminder Texts"
              status={
                !organization.settings.supress_second_reminder_sms
                  ? "Enabled"
                  : "Disabled"
              }
              isChecked={!organization.settings.supress_second_reminder_sms}
              isDisabled={localLoading.reminder1h}
              isLoading={localLoading.reminder1h}
              onChange={(checked) => {
                updateLoading("reminder1h", true);
                onUpdateSettings(
                  {
                    supress_first_reminder_sms:
                      !!organization.settings.supress_first_reminder_sms,
                    supress_second_reminder_sms: !checked,
                  },
                  checked
                    ? "1 hour reminder texts enabled"
                    : "1 hour reminder texts disabled"
                ).finally(() => {
                  updateLoading("reminder1h", false);
                });
              }}
              colorScheme="green"
            />
            <Divider />
            {rebateUiEnabled ? (
              <>
                <Stack spacing={{ base: "5", lg: "6" }}>
                  <Text fontSize="lg" fontWeight="bold" width={"full"}>
                    Rebates
                  </Text>
                  <RebateForm
                    currentRebate={getRebateFromFees(organization?.fees)}
                    onRemove={handleRebateRemove}
                    onSave={async (rebate) => {
                      updateLoading("rebate", true);
                      try {
                        const success = await updateRebate(rebate);
                        if (success) {
                          toast({
                            title: "Rebate settings updated",
                            description:
                              "Your rebate settings have been updated",
                            status: "success",
                            duration: 5000,
                            isClosable: true,
                          });
                        }
                      } finally {
                        updateLoading("rebate", false);
                      }
                    }}
                    amount={rebateConfig.amount}
                    onAmountChange={(amount) =>
                      setRebateConfig({ ...rebateConfig, amount })
                    }
                    isLoading={localLoading.rebate}
                    title="Global Rebates"
                    description="Applied to all active ticket types"
                    calculatePreviewData={calculatePreviewData}
                    formatRebateDisplay={formatRebateDisplay}
                  />
                  <Box>
                    <HStack spacing={2} align="flex-start">
                      <Icon as={FiInfo} color="gray.500" boxSize={4} mt={0.5} />
                      <VStack align="flex-start" spacing={0}>
                        <Text fontSize="sm" fontWeight="medium">
                          Event-level overrides
                        </Text>
                        <Text fontSize="sm">
                          Admins can set event specific rebates that override
                          this global setting
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                </Stack>
              </>
            ) : null}
            <Divider />
            <Stack spacing={{ base: "5", lg: "6" }}>
              <Text fontSize="lg" fontWeight="bold" width={"full"}>
                Payouts
              </Text>
              {organization.stripe_connect_id ? (
                <HStack>
                  <Icon as={FaCheck} color="green" />
                  <Text>
                    Stripe Account Linked: {organization.stripe_connect_id}
                  </Text>
                </HStack>
              ) : (
                <Stack>
                  {localLoading["stripe"] ? (
                    <HStack>
                      <Text>Linking stripe account...</Text>
                    </HStack>
                  ) : (
                    <HStack>
                      <Icon as={FaWindowClose} color="red" />
                      <Text>Stripe Account Not Linked</Text>
                    </HStack>
                  )}
                  <Button
                    isLoading={localLoading["stripe"]}
                    isDisabled={localLoading["stripe"] || !organization}
                    onClick={onLinkStripeAccount}
                  >
                    Link Stripe Account
                  </Button>
                </Stack>
              )}

              <MetaAdsSetting
                show={!!organization.ads_manager?.config_id}
                organization={organization}
                onConnect={triggerFacebookLogin}
                onDisconnect={removeOrganizationAccess}
                isLoading={localLoading["meta"]}
              />

              <Stack spacing={4} mt={8} w="full">
                <VStack w="full">
                  <Text w="full" fontWeight={"medium"}>
                    Share your organization
                  </Text>
                  <Text fontSize={"xs"}>
                    Use the id below to share with another KYD user, allowing
                    them to share events with you
                  </Text>
                </VStack>
                <HStack w="full">
                  <Box flexGrow={1}>
                    <Text>Organization ID:</Text>
                    <Text>{organization?.id}</Text>
                  </Box>

                  <IconButton
                    variant={"outline"}
                    onClick={() => {
                      onCopy();
                      showSuccessToast(toast, "Organization ID copied");
                    }}
                    icon={<FiCopy />}
                    aria-label="Copy organization id"
                  />
                </HStack>
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
    </VStack>
  );
};

export default Settings;
